/**
 * extra.json lowering: effects and videos -> IrExtraJson (W1-04 core-ir; formats-and-packaging.md P§5.1-P§5.3,
 * DESIGN.md §6.3, §7.3).
 *
 *  - null when the project has no effects, no videos and no imported extra.json;
 *  - `bpm`: the chart's BPM list as `{time, bpm}` (always written, so effect beats mean what the chart's beats mean);
 *  - effects in `effectOrder`: start / end triples (raw imported triples while equal in value), the shader
 *    (built-in name, or "/<package path>" for a custom shader asset), `global`, variables in their order;
 *  - variables: JSON null (empty anim: Phira sets the uniform to 0), a constant (float, vec2, or an RGBA vec4 with
 *    integer channels clamped to 0..255), or keyframes lowered from the owned track with extra.json keyframe rules
 *    (same key walk as line tracks; slices written as stored, unclamped; a default slice only when it was imported
 *    with both keys written, src.sliceRaw [0, 1]; NO bezier exists in extra.json, asserted);
 *  - videos (by id, i.e. creation / import order): package path, start beat, scale mode, alpha / dim (omitted for
 *    Phira's default, a constant, or keyframes), the attached line's export index, passthrough.
 */

import { Beats } from '../time/beat';
import type { Effect, EffectVar, Key, TrackId, TrackValue, Video, VideoVarValue } from '../model/types';
import { AssertionError } from '../util/assert';
import type { NonEmpty, RGBA8, Vec2 } from '../util/types';
import { extraConstantFields, extraTweenFields } from './eventFields';
import type { LowerCtx } from './lowerCtx';
import { trackKeys, walkKeys, type WalkHooks } from './lowerTrack';
import { tripleOut } from './triples';
import type { IrBpm, IrEffect, IrExtraJson, IrExtraKeyframe, IrVarValue, IrVideo } from './types';

type ExtraValue = number | Vec2 | RGBA8;

function clampU8(v: number): number {
  const r = Math.round(v);
  return Number.isNaN(r) ? 0 : r < 0 ? 0 : r > 255 ? 255 : r;
}

/** RGBA vec4 values are written as integer channels 0..255 (Phira's extra.json Color type is [u8; 4]). */
function extraValue(v: TrackValue): ExtraValue {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') throw new AssertionError('lower: an extra.json variable cannot hold text');
  if (v.length === 4) return [clampU8(v[0]), clampU8(v[1]), clampU8(v[2]), clampU8(v[3])];
  if (v.length === 2) return [v[0], v[1]];
  throw new AssertionError('lower: an extra.json variable cannot hold an RGB colour');
}

const EXTRA_HOOKS: WalkHooks<IrExtraKeyframe<ExtraValue>> = {
  gif: false,
  constant(k, span, origin) {
    const v = extraValue(k.vOut);
    return {
      event: {
        startTime: span.startTriple,
        endTime: span.endTriple,
        start: v,
        end: v,
        ...extraConstantFields(k.src),
        srcKey: k.id,
        ...(origin === 'segment' ? {} : { origin }),
      },
      end: k.vOut,
    };
  },
  segment(k: Key, n: Key, span) {
    const e = k.ease;
    if (e.kind === 'bezier') {
      throw new AssertionError(
        `lower: key ${String(k.id)} of an effect / video variable has a bezier easing (extra.json has none)`,
      );
    }
    const fields = e.kind === 'tween' ? extraTweenFields(e, k.src) : extraConstantFields(k.src);
    return {
      events: [
        {
          startTime: span.startTriple,
          endTime: span.endTriple,
          start: extraValue(k.vOut),
          end: extraValue(n.vIn),
          ...fields,
          srcKey: k.id,
        },
      ],
      end: n.vIn,
    };
  },
};

/** extra.json keyframes of a variable track (null when the track is missing or has no keys). */
export function lowerVarTrack(ctx: LowerCtx, track: TrackId): NonEmpty<IrExtraKeyframe<ExtraValue>> | null {
  const keys = trackKeys(ctx, track);
  const kfs = walkKeys(keys, EXTRA_HOOKS, ctx.acc);
  return kfs.length === 0 ? null : (kfs as unknown as NonEmpty<IrExtraKeyframe<ExtraValue>>);
}

function varValue(ctx: LowerCtx, v: EffectVar): IrVarValue {
  const val = v.value;
  switch (val.kind) {
    case 'null':
      return null;
    case 'const':
      return extraValue(val.v);
    case 'track':
      return lowerVarTrack(ctx, val.track) as IrVarValue;
  }
}

function lowerEffect(ctx: LowerCtx, e: Effect): IrEffect {
  return {
    start: tripleOut(e.start, e.src?.rawStart, ctx.acc),
    end: tripleOut(e.end, e.src?.rawEnd, ctx.acc),
    shader: e.shader.kind === 'builtin' ? e.shader.name : `/${ctx.assetPath(e.shader.asset)}`,
    vars: e.vars.map((v) => [v.name, varValue(ctx, v)] as const),
    global: e.global,
    extra: e.src?.extra ?? [],
    srcEffect: e.id,
  };
}

function videoVar(ctx: LowerCtx, v: VideoVarValue): null | number | NonEmpty<IrExtraKeyframe<number>> | undefined {
  switch (v.kind) {
    case 'default':
      return undefined;
    case 'const':
      return v.v;
    case 'track':
      return lowerVarTrack(ctx, v.track) as NonEmpty<IrExtraKeyframe<number>> | null;
  }
}

function lowerVideo(ctx: LowerCtx, v: Video): IrVideo {
  const out: { -readonly [K in keyof IrVideo]: IrVideo[K] } = {
    path: ctx.assetPath(v.asset),
    time: Beats.toTriple(v.time),
    scale: v.scale,
    extra: v.src?.extra ?? [],
    srcVideo: v.id,
  };
  const alpha = videoVar(ctx, v.alpha);
  if (alpha !== undefined) out.alpha = alpha;
  const dim = videoVar(ctx, v.dim);
  if (dim !== undefined) out.dim = dim;
  if (v.attachLine !== null) out.attach = { line: ctx.idx.lineIndex(v.attachLine) };
  return out;
}

/** The chart's BPM list (raw imported triples while equal in value). */
export function lowerBpmList(ctx: LowerCtx): NonEmpty<IrBpm> {
  const out: IrBpm[] = [];
  for (const id of ctx.idx.bpmSorted()) {
    const p = ctx.doc.bpm.get(id);
    if (p === undefined) continue;
    out.push({ bpm: p.bpm, startTime: tripleOut(p.t, p.src?.rawStart, ctx.acc), extra: p.src?.extra ?? [] });
  }
  if (out.length === 0) throw new AssertionError('lower: the document has no BPM point');
  return out as unknown as NonEmpty<IrBpm>;
}

/** `Lower.lowerExtra`. */
export function lowerExtraIr(ctx: LowerCtx, bpm: NonEmpty<IrBpm> = lowerBpmList(ctx)): IrExtraJson | null {
  const { doc } = ctx;
  if (doc.effects.size === 0 && doc.videos.size === 0 && doc.pkg.extraJsonRoot === null) return null;
  const effects: IrEffect[] = [];
  for (const id of doc.effectOrder) {
    const e = doc.effects.get(id);
    if (e !== undefined) effects.push(lowerEffect(ctx, e));
  }
  const videos = [...doc.videos.values()].sort((a, b) => a.id - b.id).map((v) => lowerVideo(ctx, v));
  const bpmOut = bpm.map((b) => ({ time: b.startTime, bpm: b.bpm }));
  return {
    bpm: bpmOut as unknown as IrExtraJson['bpm'],
    effects,
    videos,
    rootExtra: doc.pkg.extraJsonRoot ?? [],
  };
}
